/**
 * Один человек за ОДИН календарный день — собирается на клиенте, потому что ни один
 * маршрут журнала не принимает дату.
 *
 * `GET /control/logs` несёт в проте поле `period`, но до него нет пути: у маршрута нет
 * такого query-параметра, а хендлер шлёт `period: None` на каждом вызове (спека
 * гейтвея, операция «Access log timeline»; тот же факт записан в комментарии
 * `entities/log/api.ts`). У `/me/logs` фильтров нет вовсе — только страница. Значит
 * окно времени — забота клиента: фильтр по человеку сервер применит (`record_id`), а
 * границы суток мы отрезаем сами, листая ленту.
 *
 * ⚠️ **Порядок событий в ответе не является частью контракта.** Лента отдаётся
 * новыми вперёд, но обещания этого нигде нет. Журнал от него не зависит вовсе — он
 * сортирует собранное сам (`features/view-journal/lib/sort.ts`); `MyLogsFeed` зависит. Ранний выход «дальше
 * только старее» поэтому не предполагается, а ПРОВЕРЯЕТСЯ по самим страницам
 * ({@link collectDay}): пока каждая идёт от новых к старым, выход законен; как только
 * порядок нарушился, сбор продолжается до конца бюджета. Цена ошибки здесь —
 * недосчитанные проходы в дне, по которому спорят о зарплате, а это ровно тот случай,
 * где догадка недопустима.
 *
 * Границы суток — ЛОКАЛЬНЫЕ, как и всё время в кабинете: `formatClock` печатает
 * `d.getHours()`, календарь табеля рисует те же сутки. Если однажды часовой пояс
 * компании разойдётся с поясом устройства, разъедется вся эта семья экранов разом, а
 * не только этот файл.
 */
import {fetchLogs, type LogFilters} from '@/entities/log/api';
import type {AccessLogRecord, PagedLogs} from '@/entities/log/types';
import {periodBounds, type Period} from '@/entities/attendance/period';

/** Полуинтервал `[start, end)` в epoch-миллисекундах. */
export type DayWindow = {start: number; end: number};

export type DayLog = {
  /** События этого дня, по возрастанию времени: день читают вперёд, а не назад. */
  events: AccessLogRecord[];
  /** Сколько строк ленты пришлось прочитать, чтобы дойти до дня (или сдаться). */
  scanned: number;
  /**
   * Весь день здесь. `false` — бюджет просмотра кончился раньше, чем лента дошла до
   * этих суток: показанное — не «ничего не было», а «дальше не досмотрели».
   */
  complete: boolean;
};

/** Сколько строк за раз. */
const PAGE_SIZE = 100;

/**
 * Сколько страниц просмотреть, прежде чем признать, что дальше не пошли.
 *
 * `DAY_PAGES` — для одного человека за один день: у сотрудника это сотни событий в
 * год, так что две тысячи строк перекрывают несколько лет его собственной ленты.
 * `RANGE_PAGES` — для журнала компании, где за тот же период строк на два порядка
 * больше; пять тысяч не покроют месяц большой компании, и именно поэтому результат
 * несёт {@link DayLog.complete}, а экран обязан на него смотреть.
 */
const DAY_PAGES = 20;
const RANGE_PAGES = 50;

/**
 * `YYYY-MM-DD` (как его печатает движок табеля) → сутки в локальном времени.
 *
 * `new Date(y, m - 1, d + 1)` намеренно: конструктор сам переносит `32 августа` в
 * `1 сентября`, а разбор строки руками — нет.
 */
export function dayWindow(date: string): DayWindow {
  const [y, m, d] = date.split('-').map(Number);
  return {
    start: new Date(y, m - 1, d).getTime(),
    end: new Date(y, m - 1, d + 1).getTime(),
  };
}

/**
 * Листать ленту, пока день не собран.
 *
 * `page` получает НОМЕР страницы (с нуля) и размер, а не смещение: у двух маршрутов
 * разная пагинация — `limit`/`offset` у `/control/logs` и 1-based `page`/`per_page`
 * у `/me/logs`, — и пересчёт живёт у вызывающего, где видно, какой из двух это.
 */
export async function collectDay(
  window: DayWindow,
  page: (pageIndex: number, pageSize: number) => Promise<PagedLogs>,
  maxPages: number = DAY_PAGES,
): Promise<DayLog> {
  const events: AccessLogRecord[] = [];
  let scanned = 0;
  let complete = false;
  // Порядок считается «новыми вперёд», пока ни одна пара соседних строк этого не
  // опровергла — см. предупреждение в шапке файла.
  let newestFirst = true;
  let previous = Number.POSITIVE_INFINITY;

  for (let index = 0; index < maxPages; index += 1) {
    const {items, total} = await page(index, PAGE_SIZE);
    scanned += items.length;

    for (const item of items) {
      if (item.created_at_millis > previous) {
        newestFirst = false;
      }
      previous = item.created_at_millis;
      if (
        item.created_at_millis >= window.start &&
        item.created_at_millis < window.end
      ) {
        events.push(item);
      }
    }

    // Лента кончилась — либо страница пришла неполной, либо прочитано всё, что
    // сервер насчитал по этому фильтру.
    if (items.length < PAGE_SIZE || scanned >= total) {
      complete = true;
      break;
    }
    // Последняя прочитанная строка старше начала суток, и порядок пока не подводил:
    // всё, что дальше, — ещё старее.
    if (newestFirst && previous < window.start) {
      complete = true;
      break;
    }
  }

  events.sort((a, b) => a.created_at_millis - b.created_at_millis);
  return {events, scanned, complete};
}

/**
 * Все проходы одного сотрудника за один день — глазами работодателя.
 *
 * `record_id` фильтрует сервер, поэтому листать приходится только события этого
 * человека, а не всю ленту компании: месяц назад у одного сотрудника — это сотни
 * строк, а не десятки тысяч.
 */
export function fetchPersonDayLogs(
  companyId: string,
  recordId: string,
  date: string,
): Promise<DayLog> {
  return collectDay(dayWindow(date), (index, size) =>
    fetchLogs(companyId, {recordId, limit: size, offset: index * size}),
  );
}

/**
 * Произвольный отрезок календаря — те же сутки, только шире.
 *
 * `to` ВКЛЮЧИТЕЛЬНО, как и во всех отчётах явки: «с 1 по 31 августа» обязано содержать
 * 31 августа целиком, поэтому правая граница — полночь следующего дня.
 */
export function rangeWindow(from: string, to: string): DayWindow {
  const [fy, fm, fd] = from.split('-').map(Number);
  const [ty, tm, td] = to.split('-').map(Number);
  return {
    start: new Date(fy, fm - 1, fd).getTime(),
    end: new Date(ty, tm - 1, td + 1).getTime(),
  };
}

/**
 * «За всё время» — то же окно, только без границ.
 *
 * Сбор всё равно упирается в бюджет страниц ({@link fetchLogsInRange}), так что
 * ответ честнее называть «последние сколько-то тысяч событий»: именно это и говорит
 * `complete: false`, и экран обязан это напечатать. Отдельной ветки «без периода» в
 * журнале нет намеренно — иначе сортировка и три числа наверху считались бы то по
 * всей выборке, то по одной странице, и объяснить разницу было бы нечем.
 */
export const ALL_TIME: DayWindow = {
  start: Number.NEGATIVE_INFINITY,
  end: Number.POSITIVE_INFINITY,
};

/** {@link Period} (месяц или диапазон) → его границы в миллисекундах. */
export function periodWindow(period: Period): DayWindow {
  const {from, to} = periodBounds(period);
  return rangeWindow(from, to);
}

/**
 * Журнал компании за период — с теми же фильтрами, что принимает сам маршрут.
 *
 * Это единственный способ спросить «что было 14 августа»: даты сервер не принимает, а
 * `total`/`total_granted`/`total_denied` в конверте считаются по ВСЕЙ выборке фильтра,
 * без учёта периода. Поэтому экран, показывающий период, обязан считать свои три числа
 * по собранным строкам, а не по конверту, — иначе он подпишет «за 14 августа» цифру за
 * всю историю компании.
 */
export function fetchLogsInRange(
  companyId: string,
  filters: LogFilters,
  window: DayWindow,
): Promise<DayLog> {
  return collectDay(
    window,
    (index, size) =>
      fetchLogs(companyId, {...filters, limit: size, offset: index * size}),
    RANGE_PAGES,
  );
}
